import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { cityProperties } from "redux/reducers/Properties/PropertiesSlice";
import { setSearchData } from "redux/reducers/Search/SearchSlice";
import { AppDispatch } from "redux/store";
import { MapPinIcon, HomeIcon, SparklesIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
// Import your local image
import limaMainView from "../../images/lima-nomadroof-main-view.jpg";

export default function RetreatCards() {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [isSearching, setIsSearching] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [userLocation, setUserLocation] = useState<{
        latitude: number;
        longitude: number;
        locationData: { city: string; country: string; street?: string };
    } | null>(null);

    useEffect(() => {
        setUserLocation({
            latitude: -12.122486,
            longitude: -77.031354,
            locationData: { city: "Peru", country: "Peru", street: "Peru" },
        });
    }, []);

    const onSearchSubmit = async () => {
        if (!userLocation || isSearching) return;
        
        setIsSearching(true);
        const startDate = "";
        const endDate = "";
        const guestCount = 0;
        const searchQuery = `q[availability_start_lteq]=${startDate}&q[availability_end_gteq]=${endDate}&q[guest_number_gteq]=${guestCount}&q[latitude]=${userLocation.latitude}&q[longitude]=${userLocation.longitude}`;
        
        try {
            const action = await dispatch(cityProperties(searchQuery));
            const searchedResult = action.payload;
            dispatch(
                setSearchData({
                    startDate,
                    endDate,
                    totalGuests: guestCount,
                    latitude: userLocation.latitude,
                    longitude: userLocation.longitude,
                    locationData: userLocation.locationData,
                    label: `${userLocation.locationData.city}, ${userLocation.locationData.country}`,
                    searchedResult,
                })
            );
            if (searchedResult?.success) {
                navigate("/listing-stay-map");
            }
        } catch (error) {
            console.error("Search failed:", error);
        } finally {
            setIsSearching(false);
        }
    };

    return (
        <section className="py-10 px-4">
            <div className="max-w-7xl mx-auto">
                {/* Hero section */}
                <div className="text-center mb-8 space-y-3">
                    <div className="inline-flex items-center gap-2 bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 px-4 py-2 rounded-full text-sm font-medium mb-3">
                        <SparklesIcon className="w-4 h-4" />
                        Now Available in Lima
                    </div>
                    
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 dark:text-white">
                        Your Home Away From Home
                    </h2>
                    
                    <p className="text-lg md:text-xl text-neutral-600 dark:text-neutral-400 font-light max-w-2xl mx-auto">
                        Verified, community-focused student housing in Lima's best neighborhoods
                    </p>
                </div>

                {/* Main Lima Card - Hero Style with increased height */}
                <div 
                    onClick={onSearchSubmit}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    className="relative group rounded-3xl overflow-hidden shadow-2xl cursor-pointer transition-all duration-500 hover:shadow-3xl hover:scale-[1.01] mb-8 h-[500px] md:h-[550px] lg:h-[600px]"
                >
                    {/* Background Image with better positioning */}
                    <div className="absolute inset-0 overflow-hidden">
                        <img
                            src={limaMainView}
                            alt="Lima, Peru"
                            className={`
                                w-full h-full object-cover object-center
                                transition-transform duration-700 ease-out
                                ${isHovered ? 'scale-110' : 'scale-100'}
                            `}
                        />
                    </div>

                    {/* Gradient Overlays - adjusted for better visibility */}
                    <div className={`
                        absolute inset-0 
                        bg-gradient-to-t from-black/90 via-black/40 to-transparent
                        transition-opacity duration-500
                        ${isHovered ? 'opacity-95' : 'opacity-85'}
                    `} />
                    
                    {/* Decorative gradient on the side */}
                    <div className="absolute inset-y-0 right-0 w-1/3 bg-gradient-to-l from-primary-600/20 to-transparent opacity-50" />

                    {/* Content Container - adjusted for better spacing */}
                    <div className="absolute inset-0 p-6 md:p-10 pb-10 md:pb-12 flex flex-col justify-between">
                        {/* Top Section - Badge */}
                        <div className="flex justify-between items-start flex-shrink-0">
                            <span className="inline-flex items-center gap-2 bg-white/95 backdrop-blur-sm text-neutral-900 px-4 py-2 rounded-full shadow-lg font-semibold text-sm">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                                Available Now
                            </span>
                        </div>

                        {/* Bottom Section - Main Content */}
                        <div className="space-y-4">
                            {/* Location */}
                            <div className="flex items-center gap-2 text-white/90">
                                <MapPinIcon className="w-5 h-5" />
                                <span className="text-sm md:text-base font-medium">Lima, Peru</span>
                            </div>

                            {/* City Name */}
                            <h3 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white">
                                Lima
                            </h3>

                            {/* Description - only show on hover on larger screens */}
                            <p className={`
                                text-white/90 text-base md:text-lg max-w-2xl leading-relaxed
                                transition-all duration-500
                                hidden md:block
                                ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}
                            `}>
                                Where historic charm meets vibrant student life
                            </p>

                            {/* Stats Grid - compact version */}
                            <div className={`
                                flex flex-wrap gap-3 md:gap-4 max-w-2xl
                                transition-all duration-500
                                ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}
                            `}>
                                <div className="bg-white/10 backdrop-blur-md rounded-xl px-4 py-2.5 border border-white/20">
                                    <span className="text-xl font-bold text-white">15+</span>
                                    <span className="text-white/80 text-sm ml-1.5">Properties</span>
                                </div>
                                <div className="bg-white/10 backdrop-blur-md rounded-xl px-4 py-2.5 border border-white/20">
                                    <span className="text-xl font-bold text-white">$220</span>
                                    <span className="text-white/80 text-sm ml-1.5">From/mo</span>
                                </div>
                                <div className="bg-white/10 backdrop-blur-md rounded-xl px-4 py-2.5 border border-white/20">
                                    <span className="text-xl font-bold text-white">100%</span>
                                    <span className="text-white/80 text-sm ml-1.5">Verified</span>
                                </div>
                            </div>

                            {/* CTA Button - Always visible with proper spacing */}
                            <div className="pt-4">
                                <button 
                                    disabled={isSearching}
                                    className="
                                        inline-flex items-center justify-center gap-2
                                        bg-white text-neutral-900 font-bold text-base md:text-lg
                                        px-8 py-4 rounded-xl
                                        transition-all duration-300
                                        hover:bg-primary-500 hover:text-white hover:gap-3
                                        disabled:opacity-50 disabled:cursor-not-allowed
                                        shadow-xl hover:shadow-2xl
                                        w-auto
                                    "
                                >
                                    {isSearching ? (
                                        <>
                                            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Searching...
                                        </>
                                    ) : (
                                        <>
                                            Explore Properties
                                            <ArrowRightIcon className="w-5 h-5" />
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Trust Indicators */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                    <div className="bg-white dark:bg-neutral-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
                        <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mb-4">
                            <svg className="w-6 h-6 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <h4 className="text-lg font-bold text-neutral-900 dark:text-white mb-2">
                            Verified Properties
                        </h4>
                        <p className="text-neutral-600 dark:text-neutral-400 text-sm">
                            Every listing is personally inspected and verified by our team
                        </p>
                    </div>

                    <div className="bg-white dark:bg-neutral-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
                        <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mb-4">
                            <svg className="w-6 h-6 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"/>
                            </svg>
                        </div>
                        <h4 className="text-lg font-bold text-neutral-900 dark:text-white mb-2">
                            Student Community
                        </h4>
                        <p className="text-neutral-600 dark:text-neutral-400 text-sm">
                            Connect with students from around the world in safe environments
                        </p>
                    </div>

                    <div className="bg-white dark:bg-neutral-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
                        <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mb-4">
                            <svg className="w-6 h-6 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z" clipRule="evenodd"/>
                            </svg>
                        </div>
                        <h4 className="text-lg font-bold text-neutral-900 dark:text-white mb-2">
                            Extra perks
                        </h4>
                        <p className="text-neutral-600 dark:text-neutral-400 text-sm">
                            Enjoy exclusive discounts on our trips, free access to parties and social events, plus many more benefits
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}